<script lang="ts">
  import { onDestroy } from 'svelte';

  import type { SubEvaluation } from '@/teoCore/TeoApi';
  import type { AnalysisResult } from '@/teoCore/text/AnalysisResult';
  import type { TeoText } from '@/teoCore/text/TeoText';
  import CtaButton from '@/ui/elements/buttons/CTAButton.svelte';
  import OpenButton from '@/ui/elements/buttons/OpenButton.svelte';
  import Switch from '@/ui/elements/buttons/Switch.svelte';
  import ColumnHeadline from '@/ui/elements/column-headline/ColumnHeadline.svelte';
  import BrandFit, { type BrandFitStyle } from '@/ui/elements/tpi-panel/BrandFit.svelte';
  import TPIScore from '@/ui/elements/tpi-score/TPIScore.svelte';
  import { useAnalysisState } from '@/utils/teo-context/teo-atoms/analysis';
  import {
    useDynamicConfigOption,
    useTeoTextUiStateObserver,
  } from '@/utils/use-teo-event-observer.svelte';

  import BrandFitComponent from './BrandFit/BrandFitComponent.svelte';
  import DictionaryDrawerComponent from './Dictionary/DictionaryDrawerComponent.svelte';
  import PerformanceComponent from './Performance/PerformanceComponent.svelte';
  import SpellCheckComponent from './SpellCheck/SpellCheckComponent.svelte';
  import StyleComponent from './Style/StyleComponent.svelte';

  let analysisState = useAnalysisState();

  let { teoText }: { teoText: TeoText } = $props();
  let tpiResult: number | undefined = $state();
  let whiteListToggle: boolean = $state(teoText.whiteListActive);
  let whiteListAvailable = $state(teoText.wordLists.whiteList.isAvailable);
  let indicesResult: Record<string, SubEvaluation> | null = $state(null);

  let analysisLoading = $state(false);
  let showBrandFit = useDynamicConfigOption(teoText.core.dynamicConfig, 'brandFitEnabled');
  let brandFitLoading: boolean = $state(false);
  let brandFitStyle: BrandFitStyle = $state('notAnalyzed');
  let view = useTeoTextUiStateObserver(teoText.ui, 'view');
  const dimensionDrawerOpen = useTeoTextUiStateObserver(teoText.ui, 'dimensionDrawerOpen');

  if (teoText.analysis) {
    onAnalysis(teoText.analysis);
  }

  let eventListenerRemovers = [
    teoText.events.addEventListener('gotAnalysis', onAnalysis),
    teoText.events.addEventListener('analysisPending', onAnalysisPending),
    teoText.wordLists.brandFit.events.addEventListener('brandFitCheck', onBrandFitCheck),
    teoText.wordLists.events.addEventListener(
      'scanPending',
      (pending: boolean) => (brandFitLoading = pending),
    ),
    teoText.wordLists.events.addEventListener('gotScanResult', onScanResult),
  ];

  onDestroy(() => {
    eventListenerRemovers.forEach((remove) => remove());
  });

  function onWhitlistToggle(value: boolean) {
    whiteListToggle = value;
    teoText.wordLists.whiteList.isActive = value;
    if (!value) {
      teoText.wordLists.whiteList.markEntries();
    } else {
      teoText.wordLists.whiteList.unmarkEntries();
    }
  }

  function onAnalysis(result: AnalysisResult) {
    if (!tpiResult) {
      teoText.ui.update('dimensionDrawerOpen', true);
    }
    tpiResult = result.getTPI();
    indicesResult = result.getIndices();
    whiteListAvailable = result.hasWhitelistedWords();
    analysisLoading = false;
  }

  function onScanResult() {
    whiteListAvailable = teoText.wordLists.whiteList.isAvailable;
  }

  function onAnalysisPending(pending: boolean) {
    analysisLoading = pending;
  }

  function analyze() {
    teoText.analyze();
  }

  function onStatusChange(status: boolean) {
    teoText.ui.update('dimensionDrawerOpen', status);
    if (!dimensionDrawerOpen.current) {
      teoText.ui.update('textProperty', undefined);
      teoText.ui.update('dimension', undefined);
    }
  }

  function onBrandFitCheck(pass: boolean | undefined) {
    brandFitStyle =
      pass === undefined ? 'notAnalyzed'
      : pass ? 'pass'
      : 'fail';
  }

  const onBrandFitClick = () => {
    teoText.ui.update('view', 'brand-scanner');
  };
</script>

<ColumnHeadline text="TPI-Dashboard" />
<div class="relative z-10 rounded-md bg-white p-4 shadow-card">
  <div class="flex items-center">
    {#if showBrandFit.current}
      <BrandFit style={brandFitStyle} onclick={onBrandFitClick} loading={brandFitLoading}
      ></BrandFit>
    {/if}
    <div class="ml-auto">
      <Switch
        id="whiteListToggle"
        label="Whitelist"
        onCheckedChange={onWhitlistToggle}
        checked={whiteListToggle}
        disabled={!whiteListAvailable}
      />
    </div>
  </div>
  <div class="flex justify-center">
    <TPIScore modified={false} value={tpiResult}></TPIScore>
  </div>
  <div class="mt-4 w-full">
    <CtaButton
      variant="cta"
      class="w-full"
      disabled={!analysisState.current.canAnalyze || analysisState.current.isCurrent}
      loading={analysisLoading}
      onclick={analyze}>Analyse starten</CtaButton
    >
  </div>
  {#if indicesResult}
    <div class="absolute bottom-0 left-1/2 z-20 -translate-x-1/2 translate-y-[17px]">
      <OpenButton status={dimensionDrawerOpen.current} {onStatusChange}></OpenButton>
    </div>
  {/if}
</div>

<div class="-mt-2">
  {#if view.current === 'performance'}
    <PerformanceComponent {teoText}></PerformanceComponent>
  {:else if view.current === 'brand-scanner'}
    <BrandFitComponent {teoText}></BrandFitComponent>
  {:else if view.current === 'style'}
    <StyleComponent {teoText}></StyleComponent>
  {:else if view.current === 'dictionary'}
    <DictionaryDrawerComponent {teoText}></DictionaryDrawerComponent>
  {:else if view.current === 'spellcheck'}
    <SpellCheckComponent {teoText}></SpellCheckComponent>
  {/if}
</div>
